import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { PlusOutlined, DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useProducts, useDeleteProduct } from '@hooks/useDictionaries';
import { useImportProducts } from '@hooks/useImport';
import type { DsmdStatusFilter, ProductListItem, ProductFilters, RiskClass } from '@/types/dictionaries';
import { downloadProductsExcel } from '@/api/exportApi';
import ImportModal from '@modules/common/ImportModal';

const MD_RISK_CLASS_OPTIONS: { value: RiskClass | ''; label: string }[] = [
  { value: '', label: 'Всі класи' },
  { value: 'I', label: 'I' },
  { value: 'IIa', label: 'IIa' },
  { value: 'IIb', label: 'IIb' },
  { value: 'III', label: 'III' },
];

const MD_ACTIVE_OPTIONS = [
  { value: 'all', label: 'Всі' },
  { value: 'active', label: 'Активні' },
  { value: 'archived', label: 'Архівні' },
];

const MD_DSMD_OPTIONS: { value: DsmdStatusFilter | ''; label: string }[] = [
  { value: '', label: 'Всі' },
  { value: 'valid', label: 'Дійсний' },
  { value: 'expiring', label: 'Спливає (≤ 90 днів)' },
  { value: 'expired', label: 'Прострочений' },
  { value: 'none', label: 'Відсутній' },
];

const MD_RISK_CLASS_COLORS: Record<RiskClass, string> = {
  I: 'green',
  IIa: 'blue',
  IIb: 'orange',
  III: 'red',
};

const mdFormatPrice = (mdAmount?: number | null, mdCurrency?: string | null): string =>
  mdAmount !== undefined && mdAmount !== null
    ? `${mdAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}${mdCurrency ? ` ${mdCurrency}` : ''}`
    : '—';

interface ProductsPageProps {
  onEditProduct: (mdProductId: string) => void;
  onCreateProduct: () => void;
}

export default function ProductsPage({ onEditProduct, onCreateProduct }: ProductsPageProps) {
  const [mdSearch, setMdSearch] = useState('');
  const [mdRiskClass, setMdRiskClass] = useState<RiskClass | ''>('');
  const [mdActiveFilter, setMdActiveFilter] = useState('active');
  const [mdDsmdStatus, setMdDsmdStatus] = useState<DsmdStatusFilter | ''>('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);

  const mdFilters: ProductFilters = useMemo(() => ({
    search: mdSearch || undefined,
    riskClass: mdRiskClass || undefined,
    isActive: mdActiveFilter === 'all' ? undefined : mdActiveFilter === 'active',
    dsmdStatus: mdDsmdStatus || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdRiskClass, mdActiveFilter, mdDsmdStatus, mdPage, mdPageSize]);

  const [mdExportLoading, setMdExportLoading] = useState(false);
  const [mdIsImportModalOpen, setMdIsImportModalOpen] = useState(false);
  const { data: mdProductsData, isLoading: mdIsLoading } = useProducts(mdFilters);
  const mdDeleteProduct = useDeleteProduct();
  const mdImportProducts = useImportProducts();

  const mdHandleExportExcel = async () => {
    setMdExportLoading(true);
    try {
      await downloadProductsExcel();
    } finally {
      setMdExportLoading(false);
    }
  };

  const mdGetDsmdRowClass = (mdRecord: ProductListItem): string => {
    if (!mdRecord.dsmdRegistrationExpiry) return '';
    const mdExpiry = dayjs(mdRecord.dsmdRegistrationExpiry);
    const mdNow = dayjs();
    if (mdExpiry.isBefore(mdNow)) return 'md-row-dsmd-expired';
    if (mdExpiry.diff(mdNow, 'day') < 90) return 'md-row-dsmd-expiring';
    return '';
  };

  const mdGetRowClassName = (mdRecord: ProductListItem): string => {
    if (!mdRecord.isActive) return 'md-row-archived';
    return mdGetDsmdRowClass(mdRecord);
  };

  const mdColumns: ColumnsType<ProductListItem> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      sorter: (mdA, mdB) => mdA.sku.localeCompare(mdB.sku),
    },
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (mdA, mdB) => mdA.name.localeCompare(mdB.name),
    },
    {
      title: 'GTIN',
      dataIndex: 'gtin',
      key: 'gtin',
      width: 160,
    },
    {
      title: 'Клас ризику',
      dataIndex: 'riskClass',
      key: 'riskClass',
      width: 110,
      align: 'center',
      render: (mdValue: RiskClass) => (
        <Tag color={MD_RISK_CLASS_COLORS[mdValue] ?? 'default'}>{mdValue}</Tag>
      ),
    },
    {
      title: 'Од. виміру',
      dataIndex: 'unitOfMeasureName',
      key: 'unitOfMeasureName',
      width: 100,
    },
    {
      title: 'Ціна закуп.',
      dataIndex: 'defaultPurchasePrice',
      key: 'defaultPurchasePrice',
      width: 120,
      align: 'right',
      // Ціни у ProductDto поки немає (server gap) — показуємо «—»
      render: (_: unknown, mdRecord: ProductListItem) =>
        mdFormatPrice(mdRecord.defaultPurchasePrice, mdRecord.defaultPurchaseCurrency),
    },
    {
      title: 'Ціна продажу',
      dataIndex: 'defaultSalePrice',
      key: 'defaultSalePrice',
      width: 120,
      align: 'right',
      render: (_: unknown, mdRecord: ProductListItem) =>
        mdFormatPrice(mdRecord.defaultSalePrice, mdRecord.defaultSaleCurrency),
    },
    {
      title: 'ДСМД',
      key: 'dsmd',
      width: 140,
      render: (_: unknown, mdRecord: ProductListItem) => {
        if (!mdRecord.dsmdRegistrationNumber) return <Tag>Відсутній</Tag>;
        const mdExpiry = mdRecord.dsmdRegistrationExpiry
          ? dayjs(mdRecord.dsmdRegistrationExpiry)
          : null;
        const mdNow = dayjs();
        if (mdExpiry && mdExpiry.isBefore(mdNow)) {
          return <Tag color="red">Прострочений</Tag>;
        }
        if (mdExpiry && mdExpiry.diff(mdNow, 'day') < 90) {
          return <Tag color="orange">Спливає</Tag>;
        }
        return <Tag color="green">Дійсний</Tag>;
      },
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 80,
      render: (_: unknown, mdRecord: ProductListItem) => (
        <Popconfirm
          title="Видалити позицію?"
          onConfirm={() => mdDeleteProduct.mutate(mdRecord.id)}
          okText="Так"
          cancelText="Ні"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Номенклатура
        </Typography.Title>
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setMdIsImportModalOpen(true)}
          >
            Імпорт з Excel
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={mdHandleExportExcel}
            loading={mdExportLoading}
          >
            Експорт Excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateProduct}>
            Додати позицію
          </Button>
        </Space>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за SKU, назвою, GTIN..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 300 }}
        />
        <Select
          options={MD_RISK_CLASS_OPTIONS}
          value={mdRiskClass}
          onChange={(mdValue) => {
            setMdRiskClass(mdValue);
            setMdPage(1);
          }}
          style={{ width: 140 }}
          placeholder="Клас ризику"
        />
        <Select
          options={MD_ACTIVE_OPTIONS}
          value={mdActiveFilter}
          onChange={(mdValue) => {
            setMdActiveFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 140 }}
        />
        <Select
          options={MD_DSMD_OPTIONS}
          value={mdDsmdStatus}
          onChange={(mdValue) => {
            setMdDsmdStatus(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
          placeholder="ДСМД статус"
        />
      </Space>

      <style>{`
        .md-row-dsmd-expired { background-color: #fff1f0 !important; }
        .md-row-dsmd-expired:hover > td { background-color: #ffccc7 !important; }
        .md-row-dsmd-expiring { background-color: #fffbe6 !important; }
        .md-row-dsmd-expiring:hover > td { background-color: #fff1b8 !important; }
        .md-row-archived { background-color: #f5f5f5 !important; color: #999 !important; }
        .md-row-archived:hover > td { background-color: #e8e8e8 !important; }
      `}</style>

      <Table<ProductListItem>
        columns={mdColumns}
        dataSource={mdProductsData?.items}
        rowKey="id"
        loading={mdIsLoading}
        rowClassName={mdGetRowClassName}
        onRow={(mdRecord) => ({
          onClick: () => onEditProduct(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdProductsData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <ImportModal
        open={mdIsImportModalOpen}
        title="Імпорт номенклатури з Excel"
        onClose={() => setMdIsImportModalOpen(false)}
        onImport={(mdFile) => mdImportProducts.mutateAsync(mdFile)}
        isPending={mdImportProducts.isPending}
      />
    </>
  );
}
